/**
 * Connection status enumeration with enhanced states
 */
export enum ConnectionStatus {
  DISCONNECTED = 'disconnected',
  CONNECTING = 'connecting',
  CONNECTED = 'connected',
  RECONNECTING = 'reconnecting',
  FAILED = 'failed',
  CIRCUIT_OPEN = 'circuit_open',
  HIBERNATION_RECOVERY = 'hibernation_recovery',
  WIFI_CHANGE_RECOVERY = 'wifi_change_recovery',
  NETWORK_STABILIZING = 'network_stabilizing',
  APPLICATION_NAVIGATION = 'application_navigation'
}

/**
 * Network quality levels
 */
export enum NetworkQuality {
  OFFLINE = 'offline',
  POOR = 'poor',
  GOOD = 'good',
  EXCELLENT = 'excellent'
}

/**
 * Connection state interface
 */
export interface ConnectionState {
  readonly status: ConnectionStatus;
  readonly quality: NetworkQuality;
  readonly lastConnected?: Date;
  readonly reconnectAttempts: number;
  readonly lastError?: string;
  readonly isOnline: boolean;
  // Enhanced reconnection tracking
  readonly isReconnecting?: boolean;
  readonly maxReconnectAttempts?: number;
  readonly nextRetryDelay?: number;
}

/**
 * WebSocket message interface
 */
export interface WebSocketMessage {
  readonly type: string;
  readonly data?: any;
  readonly timestamp: number;
  readonly id?: string;
}

/**
 * Specific message types from server
 */
export interface HeartbeatMessage {
  readonly event_type: 'heartbeat';
  readonly timestamp: string;
}

export interface PongMessage {
  readonly event_type: 'pong';
  readonly connection_id: string;
  readonly timestamp: string;
}

export interface AckMessage {
  readonly event_type: 'ack';
  readonly user_id: string;
  readonly timestamp: string;
}

/**
 * User event message
 */
export interface UserEventMessage {
  readonly event_type: 'user_event';
  readonly event: string;
  readonly agent_id: string;
  readonly email: string;
  readonly ip_address: string;
  readonly reason: string;
  readonly timestamp: string;
}

/**
 * Conversation event message
 */
export interface ConversationEventMessage {
  readonly event_type: 'conversation_event';
  readonly conversation_id?: string;
  readonly conversationId?: string;
  readonly key_prefix?: string;
  readonly keyPrefix?: string;
  readonly start_date?: string | null;
  readonly startDate?: string | null;
  readonly client_id?: string | null;
  readonly clientId?: string | null;
  readonly channel_id?: string | null;
  readonly channelId?: string | null;
  readonly platform?: string | null;
  readonly workspace_id?: string | null;
  readonly workspaceId?: string | null;
  readonly agent_id?: string | null;
  readonly agentId?: string | null;
  readonly agent_name?: string | null;
  readonly agentName?: string | null;
  readonly status?: string | null;
  readonly end_date?: string | null;
  readonly endDate?: string | null;
  readonly accepted_date?: string | null;
  readonly acceptedDate?: string | null;
  readonly closure_reason?: string | null;
  readonly closureReason?: string | null;
  readonly campaign_id?: string | null;
  readonly campaignId?: string | null;
  readonly target_scope?: string | null;
  readonly targetScope?: string | null;
  readonly ticket_id?: string | null;
  readonly ticketId?: string | null;
  readonly metadata?: {
    readonly general_config?: {
      readonly accept_conversation: boolean;
      readonly enable_file_upload: boolean;
      readonly enable_file_download: boolean;
    };
    readonly translation_config?: {
      readonly enable: boolean;
        readonly source_language: string | null;
        readonly target_language: string | null;
      };
      readonly generalConfig?: {
        readonly acceptConversation?: boolean;
        readonly enableFileUpload?: boolean;
        readonly enableFileDownload?: boolean;
      };
      readonly translationConfig?: {
        readonly enable?: boolean;
        readonly sourceLanguage?: string | null;
        readonly targetLanguage?: string | null;
      };
  };
  readonly custom_variables?: Record<string, string>;
  readonly customVariables?: Record<string, string>;
  readonly timestamp: string;
}

/**
 * Message event message
 */
export interface MessageEventMessage {
  readonly event_type?: 'message_event';
  readonly message_id?: string;
  readonly messageId?: string;
  readonly conversation_id?: string;
  readonly conversationId?: string;
  readonly type: string;
  readonly message_type?: string;
  readonly messageType?: string;
  readonly content: string;
  readonly content_translated?: string | null;
  readonly contentTranslated?: string | null;
  readonly date_pending?: string | null;
  readonly datePending?: string | null;
  readonly date_sent?: string | null;
  readonly dateSent?: string | null;
  readonly date_delivered?: string | null;
  readonly dateDelivered?: string | null;
  readonly date_read?: string | null;
  readonly dateRead?: string | null;
  readonly status: string;
  readonly agent_id?: string | null;
  readonly agentId?: string | null;
  readonly campaign_id?: string | null;
  readonly campaignId?: string | null;
  readonly workspace_id?: string | null;
  readonly workspaceId?: string | null;
  readonly target_scope?: string | null;
  readonly targetScope?: string | null;
  readonly timestamp: string;
}

/**
 * State event message
 */
export interface StateEventMessage {
  readonly event_type?: 'state_event';
  readonly id: string;
  readonly agent_id?: string | null;
  readonly customId: string | null;
  readonly email: string;
  readonly username: string;
  readonly version: string;
  readonly ip: string;
  readonly lastUpdate: string;
  readonly createdAt: string;
  readonly role: string;
  readonly status: string;
  readonly campaigns: string[];
  readonly timestamp: string;
  readonly workspace_id?: string | null;
  readonly campaign_id?: string | null;
  readonly conversation_id?: string | null;
  readonly target_scope?: string | null;
  readonly reason?: string | null;
}

export interface StateSocketEnvelope {
  readonly type: 'data' | 'heartbeat' | 'ping' | 'pong' | 'ack' | string;
  readonly timestamp: string;
  readonly payload?: any;
}

export type ServerMessage = HeartbeatMessage | PongMessage | AckMessage | UserEventMessage | ConversationEventMessage | MessageEventMessage | StateEventMessage;

/**
 * Outgoing message types to server
 */
export interface PingMessage {
  readonly event_type: 'ping';
  readonly timestamp: string;
  readonly connection_id?: string;
}

export interface ChatMessage {
  readonly event_type: 'chat';
  readonly user_id: string;
  readonly message: string;
  readonly timestamp: string;
}

export interface StatusMessage {
  readonly event_type: 'status';
  readonly status: string;
  readonly timestamp: string;
  readonly user_id?: string;
}

export type ClientMessage = PingMessage | ChatMessage | StatusMessage;

/**
 * Message handler interface for different message types
 */
export interface MessageHandler {
  handleHeartbeat(message: HeartbeatMessage): void;
  handlePong(message: PongMessage): void;
  handleAck(message: AckMessage): void;
  handleGenericMessage(message: any): void;
}

/**
 * Connection configuration
 */
export interface ConnectionConfig {
  readonly maxReconnectAttempts: number;
  readonly baseReconnectDelay: number;
  readonly maxReconnectDelay: number;
  readonly backoffMultiplier: number;
  readonly pingInterval: number;
  readonly connectionTimeout: number;
  readonly enableCircuitBreaker: boolean;
}

/**
 * Circuit breaker state
 */
export interface CircuitState {
  readonly isOpen: boolean;
  readonly failureCount: number;
  readonly lastFailureTime?: Date;
  readonly nextRetryTime?: Date;
}

/**
 * Ping statistics interface
 */
export interface PingStats {
  readonly activePings: number;
  readonly responseRate: number;
  readonly lastResponseTime: number;
  readonly timeSinceLastResponse: number;
  readonly missedResponses: number;
  readonly averageResponseTime?: number;
}

/**
 * Connection health metrics
 */
export interface ConnectionHealth {
  readonly isHealthy: boolean;
  readonly qualityScore: number;
  readonly networkStability: number;
  readonly pingHealth: PingStats;
  readonly connectionUptime: number;
  readonly reconnectionFrequency: number;
}

/**
 * Connection event interface
 */
export interface ConnectionEvent {
  readonly type: 'status' | 'message' | 'error' | 'quality';
  readonly status?: ConnectionStatus;
  readonly quality?: NetworkQuality;
  readonly message?: string;
  readonly data?: any;
  readonly timestamp: number;
}

/**
 * Enhanced ping message with tracking
 */
export interface EnhancedPingMessage extends PingMessage {
  readonly sequence_number?: number;
  readonly expected_response_time?: number;
}

/**
 * Ping response tracking
 */
export interface PingResponse {
  readonly connection_id: string;
  readonly response_time: number;
  readonly success: boolean;
  readonly timestamp: number;
}
